
'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { MobileMenuSheet } from './MobileMenuSheet';
import { IMenuData } from '@/services/header/type';
import { useNotification } from './useNotification';
import Image from 'next/legacy/image';
import { SearchGlobalBtn } from './SearchGlobalBtn';
import LanguageSelector from '@/components/atoms/language-selector';
import { ISiteInfo } from '@/services/types/SingleTypes';
import { HEADER_COLORS } from './headerColors';
import { usePathname } from 'next/navigation';

interface IHeaderContainerProps extends IMenuData, ISiteInfo {}

// Constantes
const NOTIFICATION_DURATION = 3000;
const NOTIFICATION_MESSAGE = '⚠️ Serviço brevemente disponível.';
const SCROLL_THRESHOLD = 50;
const DESKTOP_MEDIA_QUERY = '(min-width: 1280px)';

export function HeaderContainer({
  cmsMenus,
  menuGroups,
  logo,
}: IHeaderContainerProps) {
  const { isVisible } = useNotification(
    NOTIFICATION_MESSAGE,
    NOTIFICATION_DURATION
  );

  const [scrolled, setScrolled] = useState(false);
  const [isMobileHeader, setIsMobileHeader] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<number | null>(null);
  const [closeTimeout, setCloseTimeout] = useState<NodeJS.Timeout | null>(null);

  // Scroll listener
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > SCROLL_THRESHOLD);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const handleViewportChange = () => {
      setIsMobileHeader(!mediaQuery.matches);
    };

    handleViewportChange();
    mediaQuery.addEventListener('change', handleViewportChange);

    return () => {
      mediaQuery.removeEventListener('change', handleViewportChange);
    };
  }, []);

  // Helper functions for submenu
  const handleOpenSubmenu = (idx: number) => {
    if (closeTimeout) {
      clearTimeout(closeTimeout);
      setCloseTimeout(null);
    }
    setOpenSubmenu(idx);
  };

  const handleCloseSubmenu = () => {
    const timeout = setTimeout(() => {
      setOpenSubmenu(null);
    }, 200);
    setCloseTimeout(timeout);
  };

  const handleKeepSubmenuOpen = (idx: number) => {
    if (closeTimeout) {
      clearTimeout(closeTimeout);
      setCloseTimeout(null);
    }
    setOpenSubmenu(idx);
  };

  // Logo: usa o ficheiro vindo do CMS; no mobile o gradiente escuro melhora contraste.
  const logoSrc = logo?.url;
  const logoAlt = logo?.alternativeText;
  const pathname = usePathname();
  const isHomePage = pathname === '/';
  const showMobileHeroGradient = isMobileHeader && isHomePage && !scrolled;

  const mobileHeaderBg = scrolled || !isHomePage ? 'rgba(18, 24, 27, 0.78)' : 'transparent';
  const headerBg = isMobileHeader ? mobileHeaderBg : HEADER_COLORS.background.white;

  // O menu desktop continua em fundo branco; controles mobile definem suas próprias cores.
  const textColor = HEADER_COLORS.text.dark;

  const isActive = (url?: string) => {
    if (!url) return false;
    return pathname === url || (url !== '/' && pathname?.startsWith(url));
  };
  const baseMenuLink = 'cursor-pointer uppercase transition-colors py-2 px-0 text-sm font-medium tracking-wide hover:text-[#F7941D]';
  const baseSubLink = 'block px-4 py-3 text-sm text-slate-500 hover:text-[#F7941D] transition-all duration-200';

  return (
    <>
      <motion.header
        initial={{
          height: 98,
          backgroundColor: HEADER_COLORS.background.white,
        }}
        animate={{
          height: scrolled ? 78 : 98,
          backgroundColor: headerBg,
        }}
        transition={{
          type: 'spring',
          damping: 30,
          stiffness: 200,
        }}
        className={`fixed ${scrolled ? 'top-0' : 'top-8'} left-0 w-full z-50`}
        style={{ color: textColor }}
      >
        <div className="relative w-full h-full">
          {showMobileHeroGradient && (
            <div
              className="pointer-events-none absolute inset-x-0 top-0 h-[170px] bg-[linear-gradient(180deg,rgba(0,0,0,0.68)_0%,rgba(0,0,0,0.46)_42%,rgba(0,0,0,0.18)_72%,rgba(0,0,0,0)_100%)] xl:hidden"
              aria-hidden="true"
            />
          )}

          {/* Notificação */}
          <AnimatePresence>
            {isVisible && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="fixed top-4 right-4 bg-yellow-100 text-orange-600 px-4 py-2 rounded shadow z-50"
              >
                {NOTIFICATION_MESSAGE}
              </motion.div>
            )}
          </AnimatePresence>

          <div className="container relative z-10 mx-auto flex items-center justify-between gap-3 lg:gap-6 h-full px-4 sm:px-6">
            {/* Logo */}
            {logoSrc && (
              <Link href="/" className="flex-shrink-0">
                <Image
                  width={160}
                  height={60}
                  src={logoSrc}
                  alt={logoAlt || 'logo'}
                  className={`object-contain transition-all duration-300 ${
                    scrolled 
                      ? 'h-[40px] w-auto' 
                      : 'w-[100px] sm:w-[140px] md:w-[160px] h-auto'
                  }`}
                  priority
                />
              </Link>
            )}

            {/* Menu Desktop */}
            <nav className="hidden xl:flex flex-1 justify-center">
              <ul className="flex items-center space-x-8 font-normal">
                {cmsMenus?.map((group, idx) => {
                  const hasSubMenu = group.subMenu && group.subMenu.length > 0;
                  
                  return (
                    <li 
                      key={`menu-${idx}-${group.titulo}`} 
                      className="relative"
                    >
                      <div
                        onMouseEnter={() => hasSubMenu && handleOpenSubmenu(idx)}
                        onMouseLeave={() => hasSubMenu && handleCloseSubmenu()}
                      >
                        {group.url ? (
                          group.link_extern ? (
                            <a
                              href={group.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`${baseMenuLink}`}
                              
                            >
                              {group.titulo}
                            </a>
                          ) : (
                            <Link
                              href={group.url}
                              className={`${baseMenuLink} ${isActive(group.url) ? 'text-[#F7941D]' : ''}`}
                                
                            >
                              {group.titulo}
                            </Link>
                          )
                        ) : (
                          <span
                            className={`${baseMenuLink} cursor-pointer`}
                            
                          >
                            {group.titulo}
                          </span>
                        )}
                      </div>

                      {/* Submenu desktop */}
                      {hasSubMenu && openSubmenu === idx && (
                        <div 
                          className="absolute left-0 top-full z-50 mt-4 min-w-[260px] bg-white shadow-lg border border-gray-100"
                          onMouseEnter={() => handleKeepSubmenuOpen(idx)}
                          onMouseLeave={() => handleCloseSubmenu()}
                        >
                          <div className="flex flex-col py-2">
                            {group.subMenu?.map((subItem, subIdx) => {
                              if (!subItem?.button) return null;
                              
                              const button = subItem.button;
                              return (
                                <div key={`submenu-${idx}-${subIdx}`}>
                                  {button.label && button.url ? (button.external_link ? (
                                    <a
                                      href={button.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`${baseSubLink}`}
                                    >
                                      {button.label}
                                    </a>
                                  ) : (
                                    <Link
                                      href={button.url}
                                      className={`${baseSubLink} ${isActive(button.url) ? 'text-[#F7941D]' : ''}`}
                                    >
                                      {button.label}
                                    </Link>
                                  ) ) : null}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* Ações: Busca, Idioma, Menu Mobile */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              <SearchGlobalBtn isDarkTheme={true} />

              {/* Botão Portal Kre+ - COMENTADO */}
              {/* <div className="hidden sm:flex">
                {isInternalPage ? (
                  // Páginas internas: botão visível (sempre, com ou sem scroll)
                  <Link
                    href={process.env.NEXT_PUBLIC_PORTAL_KREMAIS_URL || 'https://kremais-sta.gov.cv/'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center px-4 py-2 rounded-full text-sm font-semibold transition-all"
                    style={{
                      backgroundColor: HEADER_COLORS.primary.teal,
                      color: 'white',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '0.9';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                  >
                    Portal Kre+
                  </Link>
                ) : (
                  // Home: espaço reservado vazio (mantém layout)
                  <div className="px-4 py-2 w-28" />
                )}
              </div> */}

              <LanguageSelector isScrolled={scrolled} isDarkTheme={isMobileHeader} className="" />
              <MobileMenuSheet menuGroups={menuGroups} />
            </div>
          </div>

          {/* Sem submenu no desktop por enquanto */}
        </div>
      </motion.header>
    </>
  );
}
